import React, { useCallback, useMemo, useState } from "react";
import withAdminAuth from "../components/HOC/withAdminAuthPage";
import { PageContainer, PageHeader } from "../components/AssetBreadCrumb";
import useOperationsData, {
  useAssetComparisons,
  useComposedForecastKpis,
  useGenerationPairs,
} from "../hooks/useOperationsData";
import KpiRow from "../components/OperationsDashboard/KpiRow";
import NeedsAttentionWidget from "../components/OperationsDashboard/NeedsAttentionWidget";
import AgentsWidget from "../components/OperationsDashboard/AgentsWidget";
import GenerationForecastWidget from "../components/OperationsDashboard/GenerationForecastWidget";
import AnalystInsightWidget from "../components/OperationsDashboard/AnalystInsightWidget";
import AnalystInsightsWidget from "../components/OperationsDashboard/AnalystInsightsWidget";
import AssetMapWidget from "../components/OperationsDashboard/AssetMapWidget";
import OperationsToolbar from "../components/OperationsDashboard/OperationsToolbar";
import {
  analystSuggestions,
  composedForecastIdFromTile,
  composedForecastTileId,
  DEFAULT_TIME_WINDOW,
  isComposedForecastTile,
  loadFailureNotice,
  readKpiLayout,
  writeKpiLayout,
  type TimeWindow,
} from "../utils/operationsDashboard";

// The height a top-row widget holds rather than being sized by its content
const NARROW_WIDGET_CLASS_H = "lg:h-[22rem]";

// The right column is sized by what each card says: the analyst is an input and chips, agents six rows, answers take the rest
const ANALYST_WIDGET_CLASS_H = "lg:h-[14rem]";
const INSIGHTS_WIDGET_CLASS_H = "lg:h-[26rem]";
const AGENTS_WIDGET_CLASS_H = "lg:h-[18rem]";

// What the right column then comes to — 14 + 26 + 18 and two gaps — less the chart row, so both columns end level
const MAP_WIDGET_CLASS_H = "lg:h-[37.25rem]";

// The operations dashboard: an authored layout, not a blank canvas, and widgets never fetch — it all comes off the shared hook
const OperationsPage = () => {
  // Page state, not widget state — the whole dashboard answers for one range at a time
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(DEFAULT_TIME_WINDOW);

  // Which composed forecast the masthead is on. null is the freshest, which is where the board opens.
  const { pairs } = useGenerationPairs();
  const [pairId, setPairId] = useState<string | null>(null);
  const pair = useMemo(
    () => pairs.find((entry) => entry.composedForecastId === pairId) ?? null,
    [pairs, pairId]
  );

  const {
    needsAttention,
    agents,
    assets,
    insights,
    kpis,
    generationHeadline,
    comparison,
    isComparisonLoading,
    comparisonError,
    failedSources,
    isLoading,
    isRefreshing,
    refresh,
    patchAgentSchedule,
    updatedAt,
  } = useOperationsData(timeWindow, pair);

  // Live generation per asset, off the forecasts the assets link to — shares the tiles' cache, so a charted pair is free
  const assetComparisons = useAssetComparisons(assets, pairs);

  // The saved row: which tiles, in what order. Read once — localStorage is not a live source.
  const [layout, setLayout] = useState<string[]>(() => readKpiLayout());

  // Only the composed forecasts the user actually put on the row get fetched — each costs an S3 file
  const addedPairs = useMemo(
    () =>
      layout
        .filter(isComposedForecastTile)
        .map((id) => pairs.find((pair) => pair.composedForecastId === composedForecastIdFromTile(id)))
        .filter(Boolean) as typeof pairs,
    [layout, pairs]
  );
  const addedKpis = useComposedForecastKpis(addedPairs);

  // The saved order decides the row, minus tiles that no longer exist; nothing pins, the chart holds the generation number
  const orderedKpis = useMemo(() => {
    const byId = new Map([...kpis, ...addedKpis].map((kpi) => [kpi.id, kpi]));
    return layout.map((id) => byId.get(id)).filter(Boolean) as typeof kpis;
  }, [kpis, addedKpis, layout]);

  // Anything off the row is on offer, so removing a tile is always undoable; these are tile ids, ready to append as they are
  const available = useMemo(
    () => [
      ...kpis
        .filter((kpi) => !layout.includes(kpi.id))
        .map((kpi) => ({ id: kpi.id, name: kpi.label, kind: kpi.tag })),
      ...pairs
        .filter((pair) => !layout.includes(composedForecastTileId(pair.composedForecastId)))
        .map((pair) => ({
          id: composedForecastTileId(pair.composedForecastId),
          name: pair.composedForecastName,
          kind: "Generation vs forecast",
        })),
    ],
    [kpis, pairs, layout]
  );

  // Starter questions for the analyst, built from the signals the board is already showing
  const suggestions = useMemo(
    () => analystSuggestions(needsAttention, assets, timeWindow),
    [needsAttention, assets, timeWindow]
  );

  // Named rather than silent: a board whose fetch failed would otherwise read as an all-clear
  const failureNotice = loadFailureNotice(failedSources);

  const saveLayout = useCallback((ids: string[]) => {
    setLayout(ids);
    writeKpiLayout(ids);
  }, []);

  // Built from parts, since one toLocaleString renders "Tuesday, September 1 at 11:31" and the "at" reads as prose
  const now = new Date();
  const stamp = `${now.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  })} · ${now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;

  return (
    <PageContainer>
      {/* The shared header, so this page opens as every other does; the eyebrow carries the moment being described */}
      <PageHeader eyebrow={stamp} title="My Dashboards" className="mb-8" />

      {/* mb-5, the margin every other eai-seg strip carries — Data & Models, Custom Views and Model Templates */}
      <OperationsToolbar
        value={timeWindow}
        onChange={setTimeWindow}
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        updatedAt={updatedAt}
        className="mb-5"
      />

      {failureNotice && (
        <div
          role="alert"
          className="mb-5 rounded-eai-card border border-eai-danger-border bg-eai-danger-soft px-4 py-3 font-inter text-eai-body-sm text-eai-danger-text"
        >
          {failureNotice}
        </div>
      )}

      <KpiRow
        kpis={orderedKpis}
        isLoading={isLoading}
        available={available}
        catalogueEmpty={pairs.length === 0}
        layout={layout}
        onReorder={saveLayout}
        onAdd={(id) => saveLayout([...layout, id])}
        onRemove={(id) => saveLayout(layout.filter((entry) => entry !== id))}
      />

      {/* Single column under lg, in source order — the chart leads, then the narrow readouts, then the map */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* Two stretch columns, not two grid rows, so neither stack leaves the dead space a shared row height would */}
        <div className="flex flex-col gap-5 lg:col-span-9">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <GenerationForecastWidget
              comparison={comparison}
              headline={generationHeadline}
              error={comparisonError}
              isLoading={isComparisonLoading}
              pairs={pairs}
              pairId={pairId}
              onPairChange={setPairId}
              className={`lg:col-span-2 ${NARROW_WIDGET_CLASS_H}`}
            />

            {/* Fixed to the chart's height so the pair lines up rather than hugging its own content */}
            <NeedsAttentionWidget
              signals={needsAttention}
              windowLabel={timeWindow.label}
              isLoading={isLoading}
              incomplete={failedSources.length > 0}
              className={NARROW_WIDGET_CLASS_H}
            />
          </div>

          <AssetMapWidget
            cards={assets}
            comparisons={assetComparisons}
            isLoading={isLoading}
            className={MAP_WIDGET_CLASS_H}
          />
        </div>

        <div className="flex flex-col gap-5 lg:col-span-3">
          {/* Asks only: the agents' own answers are a click away in the analyst, not a feed on the board */}
          <AnalystInsightWidget suggestions={suggestions} className={ANALYST_WIDGET_CLASS_H} />

          {/* Between the two: the panel above asks the questions, this is what came back, the one below runs them */}
          <AnalystInsightsWidget
            insights={insights}
            isLoading={isLoading}
            className={INSIGHTS_WIDGET_CLASS_H}
          />

          <AgentsWidget
            agents={agents}
            isLoading={isLoading}
            onChanged={patchAgentSchedule}
            className={AGENTS_WIDGET_CLASS_H}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default withAdminAuth(OperationsPage);
